import React, { useState, useEffect } from "react";
import { X, Plus, Trash2 } from "lucide-react";
import secureStorage from "../../../utils/secureStorage";
import { api } from "../../../config/api";
import AppleSelect from "../../Common/AppleSelect";
import Notification from "../../Common/Notification";

/**
 * BulkVariantCreator - Component for creating multiple variants at once
 * Allows users to define attribute combinations and generate all possible variants
 */
const BulkVariantCreator = ({ productId, onClose, onSuccess }) => {
    const [step, setStep] = useState(1); // 1: Define attributes, 2: Review & Edit variants, 3: Creating
    const [attributeOptions, setAttributeOptions] = useState([
        { name: "Taille", values: ["S", "M", "L"] }
    ]);
    const [basePrice, setBasePrice] = useState("");
    const [baseUnit, setBaseUnit] = useState("");
    const [baseTax, setBaseTax] = useState("");
    const [units, setUnits] = useState([]);
    const [taxes, setTaxes] = useState([]);
    const [generatedVariants, setGeneratedVariants] = useState([]);
    const [notification, setNotification] = useState({ show: false, type: 'success', message: '' });
    const [isCreating, setIsCreating] = useState(false);

    // Predefined attribute templates
    const predefinedAttributes = [
        { name: "Taille", values: ["XS", "S", "M", "L", "XL", "XXL"] },
        { name: "Couleur", values: ["Rouge", "Bleu", "Vert", "Jaune", "Noir", "Blanc"] },
        { name: "Matière", values: ["Coton", "Polyester", "Laine", "Soie"] },
        { name: "Capacité", values: ["250ml", "500ml", "1L", "2L"] }
    ];

    useEffect(() => {
        fetchUnitsAndTaxes();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchUnitsAndTaxes = async () => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");
            const headers = { 'X-account-id': accountId.id, 'Accept': 'application/json' };

            const [unitsRes, taxesRes] = await Promise.all([
                api.get('/units', { headers }),
                api.get('/taxes', { headers })
            ]);

            const unitsData = Array.isArray(unitsRes.data) ? unitsRes.data : unitsRes.data.data || [];
            const taxesData = Array.isArray(taxesRes.data.items) ? taxesRes.data.items : taxesRes.data.data || [];

            setUnits(unitsData);
            setTaxes(taxesData);
        } catch (error) {
            console.error("Error fetching data:", error);
            showNotification('error', "Erreur lors du chargement des données");
        }
    };

    const showNotification = (type, message) => {
        setNotification({ show: true, type, message });
        if (type === 'success') {
            setTimeout(() => setNotification(prev => ({ ...prev, show: false })), 5000);
        }
    };

    // Add new attribute option
    const handleAddAttribute = () => {
        setAttributeOptions([...attributeOptions, { name: "", values: [] }]);
    };

    // Remove attribute option
    const handleRemoveAttribute = (index) => {
        if (attributeOptions.length > 1) {
            setAttributeOptions(attributeOptions.filter((_, i) => i !== index));
        }
    };

    // Update attribute name
    const handleAttributeNameChange = (index, name) => {
        const newOptions = [...attributeOptions];
        newOptions[index].name = name;
        setAttributeOptions(newOptions);
    };

    // Add value to attribute
    const handleAddValue = (attrIndex, value) => {
        if (value.trim()) {
            const newOptions = [...attributeOptions];
            if (!newOptions[attrIndex].values.includes(value.trim())) {
                newOptions[attrIndex].values.push(value.trim());
                setAttributeOptions(newOptions);
            }
        }
    };

    // Remove value from attribute
    const handleRemoveValue = (attrIndex, valueIndex) => {
        const newOptions = [...attributeOptions];
        newOptions[attrIndex].values.splice(valueIndex, 1);
        setAttributeOptions(newOptions);
    };

    // Use predefined attribute template
    const handleUsePredefined = (attrIndex, predefined) => {
        const newOptions = [...attributeOptions];
        newOptions[attrIndex] = { name: predefined.name, values: [...predefined.values] };
        setAttributeOptions(newOptions);
    };

    // Generate all possible combinations of variants
    const generateVariants = () => {
        // Validate inputs
        if (!basePrice || !baseUnit || !baseTax) {
            showNotification('error', "Veuillez remplir le prix de base, l'unité et la TVA");
            return;
        }

        const validAttributes = attributeOptions.filter(attr => attr.name && attr.values.length > 0);
        if (validAttributes.length === 0) {
            showNotification('error', "Veuillez ajouter au moins un attribut avec des valeurs");
            return;
        }

        // Generate combinations
        const combinations = generateCombinations(validAttributes);
        const variants = combinations.map((combo, index) => ({
            id: `temp-${index}`,
            name: combo.map(c => c.value).join(" - "),
            price: parseFloat(basePrice),
            attributes: combo,
            unitsId: baseUnit,
            taxId: baseTax,
            active: true,
            isStockable: true,
            isVendable: true,
            barcode: ""
        }));

        setGeneratedVariants(variants);
        setStep(2);
    };

    // Recursive function to generate all combinations
    const generateCombinations = (attributes, index = 0, current = []) => {
        if (index === attributes.length) {
            return [current];
        }

        const results = [];
        const attr = attributes[index];
        for (const value of attr.values) {
            results.push(...generateCombinations(
                attributes,
                index + 1,
                [...current, { name: attr.name, value }]
            ));
        }
        return results;
    };

    // Update generated variant
    const handleVariantChange = (index, field, value) => {
        const newVariants = [...generatedVariants];
        newVariants[index][field] = value;
        setGeneratedVariants(newVariants);
    };

    // Remove variant from list
    const handleRemoveVariant = (index) => {
        setGeneratedVariants(generatedVariants.filter((_, i) => i !== index));
    };

    // Create all variants
    const handleCreateVariants = async () => {
        try {
            setIsCreating(true);
            showNotification('loading', `Création de ${generatedVariants.length} déclinaisons...`);

            const accountId = await secureStorage.getItem("selectedAccount");
            const headers = {
                'X-account-id': accountId.id,
                'Content-Type': 'multipart/form-data',
                'Accept': 'application/json'
            };

            const promises = generatedVariants.map(async (variant) => {
                const formData = new FormData();
                formData.append("name", variant.name);
                formData.append("price", variant.price);
                formData.append("barcode", variant.barcode || "");
                formData.append("product", `/api/products/${productId}`);
                formData.append("units", `/api/units/${variant.unitsId}`);
                formData.append("taxe", `/api/taxes/${variant.taxId}`);  // field is `taxe` (singular) on ProductVariant
                formData.append("active", String(variant.active));
                formData.append("isStockable", String(variant.isStockable));
                formData.append("isVendable", String(variant.isVendable));
                formData.append("attributes", JSON.stringify(variant.attributes));
                formData.append("color", "#4DB6AC");

                return api.post('/variants', formData, { headers });
            });

            await Promise.all(promises);
            showNotification('success', `${generatedVariants.length} déclinaisons créées avec succès`);

            setTimeout(() => {
                onSuccess();
                onClose();
            }, 1500);
        } catch (error) {
            console.error("Error creating variants:", error);
            showNotification('error', "Erreur lors de la création des déclinaisons");
            setIsCreating(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Création en masse de déclinaisons
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {/* Progress Steps */}
                <div className="flex justify-center items-center p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-4">
                        <div className={`flex items-center ${step >= 1 ? 'text-primary' : 'text-gray-400'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-primary text-white' : 'bg-gray-300'}`}>
                                1
                            </div>
                            <span className="ml-2 text-sm font-medium">Attributs</span>
                        </div>
                        <div className={`w-16 h-0.5 ${step >= 2 ? 'bg-primary' : 'bg-gray-300'}`}></div>
                        <div className={`flex items-center ${step >= 2 ? 'text-primary' : 'text-gray-400'}`}>
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-primary text-white' : 'bg-gray-300'}`}>
                                2
                            </div>
                            <span className="ml-2 text-sm font-medium">Révision</span>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {step === 1 && (
                        <div className="space-y-6">
                            {/* Base Information */}
                            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
                                    Informations de base (communes à toutes les déclinaisons)
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div>
                                        <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">
                                            Prix de base *
                                        </label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={basePrice}
                                            onChange={(e) => setBasePrice(e.target.value)}
                                            placeholder="0.00"
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">
                                            Unité *
                                        </label>
                                        <AppleSelect
                                            value={baseUnit}
                                            onChange={(e) => setBaseUnit(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                                        >
                                            <option value="">Sélectionner</option>
                                            {units.map(unit => (
                                                <option key={unit.id} value={unit.id}>
                                                    {unit.name}
                                                </option>
                                            ))}
                                        </AppleSelect>
                                    </div>
                                    <div>
                                        <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">
                                            TVA *
                                        </label>
                                        <AppleSelect
                                            value={baseTax}
                                            onChange={(e) => setBaseTax(e.target.value)}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                                        >
                                            <option value="">Sélectionner</option>
                                            {taxes.map(tax => (
                                                <option key={tax.id} value={tax.id}>
                                                    {tax.value}%
                                                </option>
                                            ))}
                                        </AppleSelect>
                                    </div>
                                </div>
                            </div>

                            {/* Attributes Configuration */}
                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                                        Attributs et valeurs
                                    </h3>
                                    <button
                                        type="button"
                                        onClick={handleAddAttribute}
                                        className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                                    >
                                        + Ajouter attribut
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    {attributeOptions.map((attr, attrIndex) => (
                                        <AttributeEditor
                                            key={attrIndex}
                                            attribute={attr}
                                            index={attrIndex}
                                            onNameChange={handleAttributeNameChange}
                                            onAddValue={handleAddValue}
                                            onRemoveValue={handleRemoveValue}
                                            onRemove={handleRemoveAttribute}
                                            onUsePredefined={handleUsePredefined}
                                            predefinedAttributes={predefinedAttributes}
                                            canRemove={attributeOptions.length > 1}
                                        />
                                    ))}
                                </div>

                                <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                    <p className="text-xs text-blue-700 dark:text-blue-300">
                                        💡 Nombre total de déclinaisons qui seront générées: {" "}
                                        <strong>
                                            {attributeOptions.reduce((total, attr) =>
                                                total * (attr.values.length || 1),
                                                attributeOptions.length > 0 ? 1 : 0
                                            )}
                                        </strong>
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4">
                            <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                    {generatedVariants.length} déclinaisons générées. Vous pouvez les modifier avant la création.
                                </p>
                            </div>

                            <div className="space-y-3 max-h-96 overflow-y-auto">
                                {generatedVariants.map((variant, index) => (
                                    <div key={variant.id} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800">
                                        <div className="flex justify-between items-start mb-3">
                                            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <div>
                                                    <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">
                                                        Nom
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={variant.name}
                                                        onChange={(e) => handleVariantChange(index, 'name', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">
                                                        Prix
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={variant.price}
                                                        onChange={(e) => handleVariantChange(index, 'price', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg text-sm"
                                                    />
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveVariant(index)}
                                                className="ml-3 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {variant.attributes.map((attr, idx) => (
                                                <span key={idx} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-xs rounded-full text-gray-700 dark:text-gray-300">
                                                    {attr.name}: {attr.value}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-between items-center p-6 border-t border-gray-200 dark:border-gray-700">
                    <button
                        onClick={step === 1 ? onClose : () => setStep(1)}
                        className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        {step === 1 ? 'Annuler' : 'Retour'}
                    </button>
                    {step === 1 ? (
                        <button
                            onClick={generateVariants}
                            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                        >
                            Générer les déclinaisons
                        </button>
                    ) : (
                        <button
                            onClick={handleCreateVariants}
                            disabled={isCreating}
                            className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isCreating ? 'Création...' : `Créer ${generatedVariants.length} déclinaisons`}
                        </button>
                    )}
                </div>

                {/* Notification */}
                <Notification
                    show={notification.show}
                    type={notification.type}
                    message={notification.message}
                    onClose={() => setNotification(prev => ({...prev, show: false}))}
                />
            </div>
        </div>
    );
};

// Attribute Editor Component
const AttributeEditor = ({
    attribute,
    index,
    onNameChange,
    onAddValue,
    onRemoveValue,
    onRemove,
    onUsePredefined,
    predefinedAttributes,
    canRemove
}) => {
    const [newValue, setNewValue] = useState("");
    const [showPredefined, setShowPredefined] = useState(false);

    const handleAddValue = () => {
        if (newValue.trim()) {
            onAddValue(index, newValue);
            setNewValue("");
        }
    };

    return (
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900">
            <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                    <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">
                        Nom de l'attribut
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={attribute.name}
                            onChange={(e) => onNameChange(index, e.target.value)}
                            placeholder="Ex: Taille, Couleur"
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPredefined(!showPredefined)}
                            className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 text-sm"
                        >
                            Templates
                        </button>
                    </div>
                </div>
                {canRemove && (
                    <button
                        type="button"
                        onClick={() => onRemove(index)}
                        className="ml-3 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                )}
            </div>

            {/* Predefined Templates */}
            {showPredefined && (
                <div className="mb-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                    <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">Templates prédéfinis:</p>
                    <div className="flex flex-wrap gap-2">
                        {predefinedAttributes.map((predefined, idx) => (
                            <button
                                key={idx}
                                type="button"
                                onClick={() => {
                                    onUsePredefined(index, predefined);
                                    setShowPredefined(false);
                                }}
                                className="px-3 py-1 bg-primary/10 text-primary rounded-full text-xs hover:bg-primary/20"
                            >
                                {predefined.name} ({predefined.values.length})
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Values */}
            <div>
                <label className="text-xs text-gray-600 dark:text-gray-400 block mb-2">
                    Valeurs possibles
                </label>
                <div className="flex gap-2 mb-2">
                    <input
                        type="text"
                        value={newValue}
                        onChange={(e) => setNewValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddValue();
                            }
                        }}
                        placeholder="Ajouter une valeur"
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded-lg text-sm"
                    />
                    <button
                        type="button"
                        onClick={handleAddValue}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm"
                    >
                        <Plus className="w-4 h-4" />
                    </button>
                </div>
                <div className="flex flex-wrap gap-2">
                    {attribute.values.map((value, valueIndex) => (
                        <span
                            key={valueIndex}
                            className="inline-flex items-center gap-1 px-3 py-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full text-sm text-gray-700 dark:text-gray-300"
                        >
                            {value}
                            <button
                                type="button"
                                onClick={() => onRemoveValue(index, valueIndex)}
                                className="text-red-500 hover:text-red-700"
                            >
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default BulkVariantCreator;

